import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import DownIcon from 'src/icons/down';
import API from 'src/utils/api';
import { NETWORK_NAME_LABELS, SUPPORTED_TOKENS } from 'src/utils/constants';
import Modal from 'src/components/modal/Modal';
import QRCode from 'qrcode';
import CopyIcon from 'src/icons/copy';
import WarningIcon from 'src/icons/warning';
import Button from 'src/components/button/Button';
import Input from 'src/components/input/Input';
import PayWithWalletBtn from 'src/components/pay-with-wallet-btn/PayWithWalletBtn';
import Select from '../select/Select';
import { copyToClipboard } from 'src/utils/strings';

const networks = Object.keys(SUPPORTED_TOKENS);

const DepositModal = ({ isOpen, setIsOpen, addresses }) => {
    const [walletDepositSelected, setWalletDepositSelected] = useState(false);
    const [selectedToken, setSelectedToken] = useState();
    const [selectedNetwork, setSelectedNetwork] = useState();
    const [accounts, setAccounts] = useState(addresses);
    const [address, setAddress] = useState();
    const [qrCodeURL, setQrCodeURL] = useState();
    const [tokenAmount, setTokenAmount] = useState();
    const fetchingAccounts = useRef(false);

    useEffect(() => {
        if (addresses) return;
        if (fetchingAccounts.current) return; // make sure to run it only at first render

        const fetchAccounts = async () => {
            fetchingAccounts.current = true;
            try {
                const response = await API.get('/accounts/list');
                setAccounts(response.data.accounts);
                fetchingAccounts.current = false;
            } catch (error) {
                toast.error('Failed to fetch, please try again');
                fetchingAccounts.current = false;
            }
        };

        fetchAccounts();
    }, [addresses]);

    useEffect(() => {
        if (address) {
            QRCode.toDataURL(address, (err, url) => {
                if (!err && url) {
                    setQrCodeURL(url);
                }
            });
        }
    }, [address]);

    useEffect(() => {
        if (!selectedNetwork || !accounts?.length) return;
        const account = accounts.find((a) => a.network === selectedNetwork);
        setAddress(account.address);
    }, [accounts, selectedNetwork]);

    const onCopyAddressClick = () => {
        copyToClipboard(address);
    };

    const onBackBtnClick = () => {
        if (walletDepositSelected) return setWalletDepositSelected(false);
        if (selectedNetwork) return setSelectedNetwork(null);
    };

    const onSeeFullTokensListClick = () => {
        toast(
            (t) => (
                <p>
                    You selected the about to withdraw {NETWORK_NAME_LABELS[selectedNetwork]} network. Here is the full
                    list of the tokens we support:
                    <br />
                    <br />
                    <ul>
                        {SUPPORTED_TOKENS[selectedNetwork].map((token, i) => (
                            <li key={i}>- {token}</li>
                        ))}
                    </ul>
                    <br />
                    <br />
                    <div className="flex justify-end mt-4">
                        <Button primary text="OK" className="ml-1 w-1/2" onClick={() => toast.dismiss(t.id)} />
                    </div>
                </p>
            ),
            { duration: 100000 }
        );
    };

    const onWalletDepositClick = () => {
        setWalletDepositSelected(true);
        setSelectedToken(SUPPORTED_TOKENS[selectedNetwork][0]);
    };

    const onSelectTokenChange = (e) => {
        setSelectedToken(e.target.value);
    };

    return (
        <Modal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title="Deposit funds"
            hasBackBtn={selectedNetwork || walletDepositSelected}
            onBackBtnClick={onBackBtnClick}
            className="w-full lg:w-1/2 xl:w-1/3"
        >
            {walletDepositSelected && (
                <div>
                    <span className="text-gray-darker text-center w-full block">
                        Connect a wallet that supports the {NETWORK_NAME_LABELS[selectedNetwork]} network and select a
                        token and an amount to deposit.
                    </span>
                    <Select
                        label="Select token"
                        fieldName="selectToken"
                        className="block mt-6"
                        value={selectedToken}
                        onChange={onSelectTokenChange}
                        options={SUPPORTED_TOKENS[selectedNetwork]}
                    />
                    <Input
                        value={tokenAmount}
                        setValue={setTokenAmount}
                        label={'Amount to deposit'}
                        type={'number'}
                        fieldName={'amount'}
                        className=" mt-4"
                    />
                    {selectedToken && tokenAmount && (
                        <PayWithWalletBtn
                            amount={tokenAmount}
                            network={selectedNetwork}
                            token={selectedToken}
                            toAddress={address}
                            onComplete={() => setIsOpen(false)}
                        />
                    )}
                </div>
            )}
            {!walletDepositSelected && selectedNetwork && (
                <div className="flex flex-col items-center">
                    <span className="text-gray-darker text-center w-full block">
                        Send crypto to the following address on the{' '}
                        <span className="underline">{NETWORK_NAME_LABELS[selectedNetwork]} network</span> and it will
                        appear in your account in a few minutes
                    </span>
                    {qrCodeURL && <img src={qrCodeURL} alt="address qr code" width={200} className="block mb-6" />}
                    <span className="relative rounded-xl bg-gray-light py-3 px-5 w-full overflow-hidden">
                        {address}&nbsp;
                        <button
                            onClick={onCopyAddressClick}
                            className="absolute right-0 bg-gray-light rounded-full px-2"
                        >
                            <CopyIcon />
                        </button>
                    </span>
                    <p className="mt-8 text-center">
                        <WarningIcon color={'red'} className="inline-block mr-1" />
                        Only send tokens that are accepted by our platform, otherwise you won't have access to it. See
                        the full list of supported tokens for the {NETWORK_NAME_LABELS[selectedNetwork]} network{' '}
                        <button className="underline" onClick={onSeeFullTokensListClick}>
                            here
                        </button>
                        .
                    </p>
                    {selectedNetwork !== 'BTC' && (
                        <button className="underline font-bold mt-10 on" onClick={onWalletDepositClick}>
                            Option 2: Deposit using your wallet
                        </button>
                    )}
                </div>
            )}
            {!walletDepositSelected && !selectedNetwork && (
                <>
                    <span className="text-gray-darker text-center w-full block">Select network</span>
                    {networks.map((network, i) => (
                        <button
                            className="bg-gray-light rounded-xl flex justify-between items-center px-6 py-2 mt-2 mx-1 w-3/4 mx-auto"
                            key={i}
                            onClick={() => setSelectedNetwork(network)}
                        >
                            <img
                                src={`/crypto-icons/${SUPPORTED_TOKENS[network][0].toLowerCase()}.svg`}
                                alt={`${SUPPORTED_TOKENS[network][0]} icon`}
                                width={30}
                                className="mr-2"
                            />
                            <span className="">{NETWORK_NAME_LABELS[network]}</span>
                            <DownIcon className="rotate-270" color={'#000000'} />
                        </button>
                    ))}
                </>
            )}
        </Modal>
    );
};

export default DepositModal;
